import type { DropDownResult, DynamicFieldDataTypes } from '@myosh/odin-components';
import { DynamicFormRequiredType, ErrorLabel, IconButton, Input, OdinIconSize } from '@myosh/odin-components';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { SchedulerRuleField } from '../../../@types/scheduler-record';
import { forceAssert } from '../../../utils/type-guards';
import OdinDropDownComponent from '../custom-drop-downs/odin-drop-down.component';

type FieldRuleFieldProps = {
  fieldRule?: SchedulerRuleField;
  className?: string;
  componentOptions?: DynamicFieldDataTypes;
  errorObject?: Record<string, unknown>;
  onChange?: (value: SchedulerRuleFieldState, type: string) => void;
  onFieldDelete?: (value: SchedulerRuleFieldState) => void;
};

export interface SchedulerRuleFieldState extends Omit<SchedulerRuleField, 'conditionField'> {
  conditionField: {
    text: string;
    value: number;
  };
}

const FieldRuleField = ({
  fieldRule,
  componentOptions,
  errorObject,
  className,
  onChange,
  onFieldDelete,
}: FieldRuleFieldProps) => {
  const { t } = useTranslation();
  const [formState, setFormState] = useState<SchedulerRuleFieldState>({} as SchedulerRuleFieldState);

  useEffect(() => {
    if (fieldRule) {
      setFormState(fieldRule);
      if (String(fieldRule?.id).includes('new')) {
        onChange?.(fieldRule, 'field-condition');
      }
    }
  }, []);

  const handleDropDownChange = (name: keyof typeof formState) => (result: DropDownResult) => {
    const updatedState = {
      ...formState,
      [name]: result && 'value' in result ? result.originalData : undefined,
      ...(name === 'conditionField' && result.value ? { conditionFieldId: Number(result.value) || undefined } : {}),
    };
    onChange?.(updatedState, 'field-condition');
    setFormState(updatedState);
  };

  const handleInputChange = (name: keyof typeof formState) => (value?: string | number) => {
    const updatedState = {
      ...formState,
      [name]: value,
    };
    onChange?.(updatedState, 'field-condition');
    setFormState(updatedState);
  };

  const onDeleteHandler = () => {
    onFieldDelete?.(formState);
  };
  const ruleCardStyles = clsx('odin-flex-row space-x-2', className, {
    'odin-border-gray-4': !errorObject,
    'odin-border-error': errorObject,
  });

  return (
    <>
      <div className={ruleCardStyles} style={{ alignItems: 'end' }}>
        <OdinDropDownComponent
          value={formState.conditionField}
          valueField="value"
          textField="text"
          className="self-end"
          label={t('component')}
          data={componentOptions}
          readOnly={false}
          error={forceAssert<string>(Boolean(errorObject?.conditionField))}
          required={DynamicFormRequiredType.True}
          onChange={(value) => handleDropDownChange('conditionField')(value as DropDownResult)}
        />
        <Input
          label={t('first-occurence')}
          name="firstOccurrence"
          value={String(formState?.firstOccurrence)}
          type="number"
          readOnly={false}
          error={forceAssert<string>(Boolean(errorObject?.firstOccurrence))}
          required={DynamicFormRequiredType.True}
          onChange={handleInputChange('firstOccurrence')}
        />
        <Input
          label={t('repeat-every')}
          name="repeatEvery"
          value={String(formState?.repeatEvery)}
          type="number"
          readOnly={false}
          error={forceAssert<string>(Boolean(errorObject?.repeatEvery))}
          onChange={handleInputChange('repeatEvery')}
        />
        <IconButton icon="Trash" iconSize={OdinIconSize.Small} onClick={onDeleteHandler} />
      </div>
      {errorObject && <ErrorLabel>{t('record-validation-message')}</ErrorLabel>}
    </>
  );
};

export default FieldRuleField;
